import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError, formatGHS } from "@/lib/api";
import { isFinishedGoodInventoryItem } from "@/lib/inventoryProducts";
import { printElement } from "@/lib/docExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Minus, Trash2, ShoppingCart, Printer, Search, XCircle, Banknote, Smartphone, CreditCard, Building2, Settings2, Sparkles, ShieldCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ServicesCatalogModal from "@/components/ServicesCatalogModal";
import CustomerPicker from "@/components/CustomerPicker";
import BranchSelector from "@/components/BranchSelector";
import PharmacyPOSPrescriptions from "@/components/PharmacyPOSPrescriptions";
import { ReturnDialog, ShiftPanel, SplitPaymentEditor, splitPayload } from "@/components/POSExtras";

const PAYMENT_METHODS = [
  { value: "Cash", label: "Cash", icon: Banknote },
  { value: "Mobile_Money", label: "Mobile Money", icon: Smartphone },
  { value: "Card", label: "Card", icon: CreditCard },
  { value: "Bank_Transfer", label: "Bank Transfer", icon: Building2 },
  { value: "Credit", label: "Credit / Unpaid", icon: CreditCard },
  { value: "Insurance", label: "Insurance", icon: ShieldCheck },
];

export default function POS({ mode = "product" }) {
  const isServices = mode === "services";
  const isPharmacy = mode === "pharmacy";
  const { tenant, user } = useAuth();
  const location = useLocation();
  const [pharmacyTab, setPharmacyTab] = useState("retail");
  const [pharmacyPatients, setPharmacyPatients] = useState([]);
  const [pharmacyBillingPatientId, setPharmacyBillingPatientId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [inventory, setInventory] = useState([]);
  const [services, setServices] = useState([]);
  const [sales, setSales] = useState([]);
  const [totalSales, setTotalSales] = useState(0);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salePage, setSalePage] = useState(0);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [insurancePayerType, setInsurancePayerType] = useState("NHIS");
  const [insurancePayerName, setInsurancePayerName] = useState("");
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [claimReference, setClaimReference] = useState("");
  const [prescriptionContext, setPrescriptionContext] = useState(null);
  const [pharmacists, setPharmacists] = useState([]);
  const [dispensingPharmacistId, setDispensingPharmacistId] = useState("");
  const [tendered, setTendered] = useState(0);
  const [splitPayments, setSplitPayments] = useState([]);
  const [returningSale, setReturningSale] = useState(null);
  const [applyTaxes, setApplyTaxes] = useState(false);
  const [taxInclusive, setTaxInclusive] = useState(true);
  useEffect(() => {
    api.get("/settings/tax").then(({ data }) => {
      if (data?.vat_registered === true) setApplyTaxes(true);
      if (typeof data?.prices_include_tax === "boolean") setTaxInclusive(data.prices_include_tax);
    }).catch(() => {});
  }, []);
  const [manualItemOpen, setManualItemOpen] = useState(false);
  const [manualItem, setManualItem] = useState({ name: "", quantity: 1, unit_price: 0 });
  const [receipt, setReceipt] = useState(null);
  const receiptRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [collectingSale, setCollectingSale] = useState(null);
  const [collectionForm, setCollectionForm] = useState({ amount: "", payment_method: "Cash", reference: "" });

  const salesPageSize = 30;
  const loadSalesPage = async (pageIndex = 0) => {
    const params = {
      sale_type: isServices ? "service" : isPharmacy ? "pharmacy" : "product",
      offset: pageIndex * salesPageSize,
      limit: salesPageSize,
    };
    if (isPharmacy && branchId) params.branch_id = branchId;
    setSalesLoading(true);
    try {
      const response = await api.get("/pos/sales/page", { params });
      setSales(Array.isArray(response.data?.items) ? response.data.items : []);
      setTotalSales(Number(response.data?.total || 0));
    } finally {
      setSalesLoading(false);
    }
  };

  const load = async () => {
    if (isServices) {
      const [svc, , sm] = await Promise.all([
        api.get("/services"),
        loadSalesPage(0),
        api.get("/pos/summary", { params: { sale_type: "service" } }),
      ]);
      setInventory([]);
      setServices(svc.data);
      setSummary(sm.data);
    } else if (isPharmacy) {
      if (tenant?.multi_branch_enabled && !branchId) return;
      const params = { branch_id: branchId || undefined };
      const [pharmacyServices, medicines, , sm] = await Promise.all([
        api.get("/pharmacy/services"),
        api.get("/hospital/pharmacy/medicines", { params }),
        loadSalesPage(0),
        api.get("/pos/summary", { params: { sale_type: "pharmacy" } }),
      ]);
      setInventory(medicines.data);
      setServices(pharmacyServices.data);
      setSummary(sm.data);
    } else {
      const [i, , sm] = await Promise.all([
        api.get("/inventory"),
        loadSalesPage(0),
        api.get("/pos/summary", { params: { sale_type: "product" } }),
      ]);
      const items = Array.isArray(i.data) ? i.data : [];
      setInventory(items.filter(isFinishedGoodInventoryItem));
      setSummary(sm.data);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [isPharmacy, branchId]);

  useEffect(() => {
    api.get("/settings/insurance-companies")
      .then((response) => {
        const companies = (response.data?.insurance_companies || []).filter((company) => company.is_active !== false);
        setInsuranceCompanies(companies);
        if (companies.length && !insurancePayerName) {
          setInsurancePayerName(companies[0].company_name);
          if (companies[0].payer_type !== "BOTH") setInsurancePayerType(companies[0].payer_type);
        }
      })
      .catch(() => setInsuranceCompanies([]));
  }, [isPharmacy, insurancePayerName]);

  useEffect(() => {
    if (!isPharmacy || pharmacyTab !== "services-fees") return;
    api.get("/hospital/patients", { params: { branch_id: branchId || undefined } })
      .then((response) => setPharmacyPatients(response.data || []))
      .catch(() => setPharmacyPatients([]));
  }, [isPharmacy, pharmacyTab, branchId]);

  useEffect(() => {
    const prescriptionId = new URLSearchParams(location.search).get("prescription_id");
    if (!isPharmacy || !prescriptionId || (tenant?.multi_branch_enabled && !branchId)) {
      setPrescriptionContext(null);
      return;
    }
    const params = { branch_id: branchId || undefined };
    Promise.all([
      api.get("/hospital/pharmacy/prescriptions", { params }),
      api.get("/hospital/pharmacy/medicines", { params }),
      api.get("/hospital/pharmacy/pharmacists", { params }),
    ]).then(([prescriptionResponse, medicineResponse, pharmacistResponse]) => {
      const prescription = (prescriptionResponse.data || []).find((row) => row.prescription_id === prescriptionId);
      if (!prescription) throw new Error("Prescription not found in this branch");
      const medicinesById = new Map((medicineResponse.data || []).map((medicine) => [medicine.medicine_id, medicine]));
      const lines = (prescription.items || []).flatMap((line, prescriptionLineIndex) => {
        const remaining = Math.max(Number(line.quantity) - Number(line.dispensed_quantity || 0), 0);
        const medicine = medicinesById.get(line.medicine_id);
        if (!remaining || !medicine) return [];
        return [{
          item_id: null,
          medicine_id: line.medicine_id,
          name: medicine.name,
          quantity: remaining,
          unit_price: Number(medicine.unit_price) || 0,
          cash_unit_price: Number(medicine.unit_price) || 0,
          nhis_unit_price: medicine.nhis_unit_price == null ? null : Number(medicine.nhis_unit_price),
          prescription_id: prescription.prescription_id,
          prescription_line_index: prescriptionLineIndex,
          payer_type: "CASH",
          sales_unit: medicine.sales_unit || medicine.unit || "pack",
          dose: line.dose,
          frequency: line.frequency,
          duration: line.duration,
          instructions: line.instructions,
        }];
      });
      if (!lines.length) throw new Error("This prescription has no outstanding items");
      const patientRecord = prescription.patient || {};
      const patientMeta = {
        ...patientRecord,
        name: patientRecord.name || prescription.patient_name || "Patient",
        patient_no: patientRecord.patient_no || prescription.patient_no || patientRecord.reference_number || prescription.reference_number || patientRecord.patient_id || prescription.patient_id || prescription.prescription_id || "",
      };

      setCart(lines);
      setPrescriptionContext({ ...prescription, patient: patientMeta });
      setPharmacists(pharmacistResponse.data || []);
      setDispensingPharmacistId(prescription.pharmacist_id || "");
      setCustomer(patientMeta.name || "Patient");
      setCustomerId("");
      setPaymentMethod("Cash");
      const defaultInsurer = insuranceCompanies[0];
      if (defaultInsurer) {
        setInsurancePayerName(defaultInsurer.company_name);
        if (defaultInsurer.payer_type !== "BOTH") setInsurancePayerType(defaultInsurer.payer_type);
      } else {
        setInsurancePayerName("");
      }
      setClaimReference(patientMeta.patient_no);
      setPharmacyTab("retail");
    }).catch((error) => {
      setPrescriptionContext(null);
      toast.error(formatApiError(error));
    });
  }, [location.search, branchId, isPharmacy, tenant?.multi_branch_enabled, insuranceCompanies]);

  const totalSalesPages = Math.max(1, Math.ceil(totalSales / salesPageSize));
  const visibleSales = sales;
  const goToSalesPage = (pageIndex) => {
    setSalePage(pageIndex);
    loadSalesPage(pageIndex).catch((error) => toast.error(formatApiError(error)));
  };

  const filteredInv = useMemo(() => {
    if (!q) return inventory;
    const s = q.toLowerCase();
    return inventory.filter((i) => i.name?.toLowerCase().includes(s) || i.sku?.toLowerCase().includes(s) || i.item_code?.toLowerCase().includes(s));
  }, [inventory, q]);

  const filteredServices = useMemo(() => {
    const active = services.filter((s) => s.is_active !== false);
    if (!q) return active;
    const s = q.toLowerCase();
    return active.filter((sv) => sv.name?.toLowerCase().includes(s) || sv.service_code?.toLowerCase().includes(s) || sv.description?.toLowerCase().includes(s));
  }, [services, q]);

  // Product POS uses the product's configured selling_price.
  // It no longer calculates a selling price from unit_cost.
  const addToCart = (item) => {
    const sellingPrice = Number(isPharmacy ? item.unit_price : item.selling_price) || 0;

    setCart((prev) => {
      const itemKey = isPharmacy ? item.medicine_id : item.item_id;
      const existing = prev.find((c) => (isPharmacy ? c.medicine_id : c.item_id) === itemKey);
      if (existing) {
        return prev.map((c) => (isPharmacy ? c.medicine_id : c.item_id) === itemKey ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, {
        item_id: isPharmacy ? null : item.item_id,
        medicine_id: isPharmacy ? item.medicine_id : null,
        item_code: item.item_code || item.product_code || "",
        sku: item.sku || "",
        name: item.name,
        quantity: 1,
        unit_price: sellingPrice,
      }];
    });
  };

  const addServiceToCart = (svc) => {
    const isServiceFee = isPharmacy && pharmacyTab === "services-fees";
    setCart((prev) => {
      const existing = prev.find((c) => c.item_id === svc.service_id && c.is_service_fee === isServiceFee);
      if (existing) {
        return prev.map((c) => c.item_id === svc.service_id && c.is_service_fee === isServiceFee ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, { item_id: svc.service_id, name: svc.name, quantity: 1, unit_price: Number(svc.unit_price) || 0, is_service_fee: isServiceFee }];
    });
  };

  const addManualItem = () => {
    if (!manualItem.name || manualItem.quantity <= 0 || manualItem.unit_price <= 0) {
      toast.error("Fill in all fields");
      return;
    }
    setCart((prev) => [...prev, { item_id: null, ...manualItem }]);
    setManualItem({ name: "", quantity: 1, unit_price: 0 });
    setManualItemOpen(false);
  };

  const updateQty = (idx, delta) => {
    setCart((prev) => prev.map((c, i) => {
      if (i !== idx) return c;
      if (c.is_service_fee) return { ...c, quantity: 1 };
      const nq = c.quantity + delta;
      return nq <= 0 ? null : { ...c, quantity: nq };
    }).filter(Boolean));
  };

  const updatePrice = (idx, price) => {
    setCart((prev) => prev.map((c, i) => i === idx ? { ...c, unit_price: Number(price) } : c));
  };

  const removeLine = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));
  const clearCart = () => {
    setCart([]);
    setTendered(0);
    setCustomer("");
    setCustomerId("");
    setInsurancePayerName("");
    setClaimReference("");
    setPaymentMethod("Cash");
  };

  const totals = useMemo(() => {
    const gross = cart.reduce((s, i) => s + Number(i.quantity) * Number(i.unit_price || 0), 0);
    // Ghana cascading tax factor when applyTaxes is on:
    //   base -> +NHIL 2.5% -> +GETFund 2.5% -> +VAT 15% (VAT applies on base+NHIL+GETFund)
    //   factor = 1 + 0.025 + 0.025 + 0.15 * 1.05 = 1.2075
    const FACTOR = 1.2; // VAT Act 2025 (Act 1151): VAT 15% + NHIL 2.5% + GETFund 2.5% on the same base
    let subtotal, nhil, getf, vat, total;
    if (applyTaxes && taxInclusive) {
      // Gross entered includes all taxes → back out the base
      subtotal = gross / FACTOR;
      nhil = subtotal * 0.025;
      getf = subtotal * 0.025;
      vat = subtotal * 0.15;
      total = gross;
    } else if (applyTaxes) {
      subtotal = gross;
      nhil = subtotal * 0.025;
      getf = subtotal * 0.025;
      vat = subtotal * 0.15;
      total = subtotal + nhil + getf + vat;
    } else {
      subtotal = gross;
      nhil = 0; getf = 0; vat = 0;
      total = gross;
    }
    const change = Math.max(0, Number(tendered || 0) - total);
    return {
      subtotal: round2(subtotal),
      nhil: round2(nhil),
      getf: round2(getf),
      vat: round2(vat),
      total: round2(total),
      change: round2(change),
    };
  }, [cart, applyTaxes, taxInclusive, tendered]);

  const isHospitalServiceFeeSale = isPharmacy && pharmacyTab === "services-fees";

  useEffect(() => {
    setSalePage(0);
  }, [isServices, isPharmacy, branchId, location.search]);

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (isHospitalServiceFeeSale && !pharmacyBillingPatientId) return toast.error("Select a patient before checking out service fees");
    if (paymentMethod === "Credit" && !customerId) return toast.error("Select a customer for credit sales");
    if (paymentMethod === "Insurance" && !insurancePayerName.trim()) return toast.error("Enter the insurance payer name");
    if (paymentMethod === "Cash" && tendered > 0 && tendered < totals.total) return toast.error("Tendered amount is less than total");
    let splitRows = null;
    try {
      splitRows = ["Insurance", "Credit"].includes(paymentMethod) ? null : splitPayload(splitPayments, totals.total);
    } catch (err) {
      return toast.error(err.message);
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/pos/sales", {
        customer_id: customerId || null,
        customer_name: customer || (isServices ? "Walk-in Client" : "Walk-in Customer"),
        items: cart.map((c) => ({
          item_id: c.item_id || null,
          medicine_id: c.medicine_id || null,
          item_code: c.item_code || c.product_code || "",
          sku: c.sku || "",
          name: c.name,
          quantity: Number(c.quantity),
          unit_price: Number(c.unit_price),
          prescription_id: c.prescription_id || null,
          prescription_line_index: c.prescription_line_index ?? null,
          payer_type: c.prescription_id ? (paymentMethod === "Insurance" ? insurancePayerType : "CASH") : null,
          pharmacist_id: c.prescription_id ? dispensingPharmacistId || null : null,
        })),
        payment_method: paymentMethod,
        payments: splitRows || undefined,
        amount_tendered: ["Insurance", "Credit"].includes(paymentMethod) ? 0 : Number(tendered || totals.total),
        insurance_payer_name: paymentMethod === "Insurance" ? insurancePayerName.trim() : null,
        claim_reference: paymentMethod === "Insurance" ? claimReference.trim() || null : null,
        apply_taxes: applyTaxes,
        tax_inclusive: taxInclusive,
        sale_type: isServices ? "service" : isHospitalServiceFeeSale ? "hospital_service" : isPharmacy ? "pharmacy" : "product",
        branch_id: isPharmacy ? branchId || undefined : undefined,
      });

      if (isHospitalServiceFeeSale && pharmacyBillingPatientId) {
        const chargePayloads = cart.map((line) => {
          const itemTotal = Number(line.quantity || 1) * Number(line.unit_price || 0);
          const payerType = paymentMethod === "Insurance" ? (insurancePayerType === "NHIS" ? "NHIS" : "PRIVATE_INSURANCE") : "CASH";
          return {
            encounter_id: null,
            category: "SERVICE_FEE",
            description: line.name,
            service_code: line.item_id || `svc-${line.name}`,
            quantity: Number(line.quantity || 1),
            unit_price: Number(line.unit_price || 0),
            payer: payerType,
            payer_amount: paymentMethod === "Insurance" ? itemTotal : 0,
            patient_amount: paymentMethod === "Insurance" ? 0 : itemTotal,
            source_type: "hospital_service_fee",
            source_id: line.item_id || line.name,
            branch_id: branchId || undefined,
          };
        });

        await Promise.all(chargePayloads.map(async (payload) => {
          const chargeResponse = await api.post(`/hospital/patients/${pharmacyBillingPatientId}/billing/charges`, payload);
          if (payload.payer === "CASH") {
            await api.post(`/hospital/patients/${pharmacyBillingPatientId}/billing/payments`, {
              amount: Number(payload.patient_amount || payload.unit_price * payload.quantity),
              method: "CASH",
              reference: data.receipt_number,
              notes: `Hospital service fee payment — ${payload.description}`,
              charge_ids: [chargeResponse.data.charge_id],
              branch_id: branchId || undefined,
            });
          }
        }));
      }

      toast.success(`Receipt ${data.receipt_number} completed`);
      if (data.payment_status === "UNPAID") {
        toast.info(`${paymentMethod === "Credit" ? "Credit sale" : "Insurance balance"} ${formatGHS(data.balance)} added to Accounts Receivable`);
      }
      setReceipt(data);
      setSplitPayments([]);
      clearCart();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const voidSale = async (sale) => {
    if (!window.confirm(`Void receipt ${sale.receipt_number}? This will reverse the journal entry and restore stock.`)) return;
    try {
      await api.post(`/pos/sales/${sale.sale_id}/void`);
      toast.success("Sale voided");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const receiveSalePayment = async () => {
    if (!collectingSale || !Number(collectionForm.amount)) return toast.error("Enter a payment amount");
    try {
      await api.post(`/pos/sales/${collectingSale.sale_id}/receive-payment`, {
        ...collectionForm,
        amount: Number(collectionForm.amount),
      });
      toast.success(collectingSale.payment_method === "Credit" ? "Credit sale payment recorded" : "Insurance collection recorded");
      setCollectingSale(null);
      setCollectionForm({ amount: "", payment_method: "Cash", reference: "" });
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return (
    <div className="space-y-4" data-testid="pos-page">
      <ShiftPanel />
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            Operations / {isServices ? "Services POS" : isPharmacy ? "Pharmacy POS" : "Product Sales POS"}
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            {isServices ? "Services Invoicing POS" : isPharmacy ? "Pharmacy Retail POS" : "Product Sales POS"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isServices
              ? "Ring up service revenue (services, consulting, repairs, subscriptions) — auto-posts a balanced JE without touching inventory."
              : isPharmacy ? "Sell pharmacy catalog items with expiry-aware batch stock, receipts, and accounting." : "Ring up sales fast — auto-posts a balanced journal entry and reduces inventory in real time."}
          </p>
        </div>
        {summary && (
          <div className="grid grid-cols-2 gap-2 text-right">
            <div className="border border-border rounded-md px-3 py-2 bg-card">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Today's Sales</div>
              <div className="font-mono font-semibold">{summary.sales_count}</div>
            </div>
            <div className="border border-brand rounded-md px-3 py-2 bg-card">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Today's Revenue</div>
              <div className="font-mono font-semibold">{formatGHS(summary.total_revenue)}</div>
            </div>
          </div>
        )}
      </div>

      {isPharmacy && <div className="flex flex-wrap items-end justify-between gap-3">
        <BranchSelector value={branchId} onChange={setBranchId} />
        <div className="flex border-b border-border" role="tablist" aria-label="Pharmacy POS views">
          {[{ id: "retail", label: "Retail sales" }, { id: "services-fees", label: "Services & Fees" }, { id: "prescriptions", label: "Prescriptions" }].map((tab) => <button key={tab.id} type="button" role="tab" aria-selected={pharmacyTab === tab.id} onClick={() => setPharmacyTab(tab.id)} className={`border-b-2 px-3 py-2 text-sm ${pharmacyTab === tab.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`pharmacy-pos-tab-${tab.id}`}>{tab.label}</button>)}
        </div>
      </div>}

      {isPharmacy && pharmacyTab === "services-fees" && (
        <div className="rounded-md border border-border bg-card p-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div className="space-y-1">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Hospital patient billing</p>
              <p className="text-sm text-muted-foreground">Select a patient to link each service-fee item to the patient billing ledger.</p>
            </div>
            <div className="w-full md:max-w-xs">
              <Label className="text-[10px] font-mono uppercase tracking-widest">Linked patient</Label>
              <Select value={pharmacyBillingPatientId} onValueChange={setPharmacyBillingPatientId}>
                <SelectTrigger className="mt-1 h-9" data-testid="pharmacy-service-fee-patient-select"><SelectValue placeholder="Choose patient" /></SelectTrigger>
                <SelectContent>
                  {pharmacyPatients.map((patient) => (
                    <SelectItem key={patient.patient_id} value={patient.patient_id}>{patient.name} · {patient.patient_no}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {isPharmacy && prescriptionContext && <section className="flex flex-wrap items-start justify-between gap-3 border border-primary/40 bg-primary/5 p-4" data-testid="pharmacy-pos-prescription-context">
        <div><p className="text-xs font-mono uppercase text-muted-foreground">Prescription checkout</p><h2 className="font-semibold">{prescriptionContext.patient?.name || prescriptionContext.patient_name || "Patient"}</h2><p className="text-sm text-muted-foreground">{prescriptionContext.patient?.patient_no || prescriptionContext.reference_number || prescriptionContext.patient_id || "No patient reference"} · {prescriptionContext.prescription_id} · {prescriptionContext.source_type || "HOSPITAL"}</p></div>
        {!prescriptionContext.pharmacist_id && <label className="min-w-56 text-xs font-medium">Dispensing pharmacist<select aria-label="Dispensing pharmacist" className="mt-1 h-9 w-full border border-input bg-background px-2 text-sm" value={dispensingPharmacistId} onChange={(event) => setDispensingPharmacistId(event.target.value)}><option value="">Select pharmacist</option>{pharmacists.map((pharmacist) => <option key={pharmacist.pharmacist_id} value={pharmacist.pharmacist_id}>{pharmacist.name}</option>)}</select></label>}
        {prescriptionContext.interaction_alerts?.length > 0 && <div className="text-xs text-amber-800">{prescriptionContext.interactions_reviewed ? "Interaction review acknowledged" : "Interaction review is required before checkout"}</div>}
      </section>}

      {isPharmacy && pharmacyTab === "prescriptions" ? <PharmacyPOSPrescriptions branchId={branchId} /> : <>
      <div className={`grid grid-cols-1 lg:grid-cols-5 gap-4`}>
        {!isServices && !prescriptionContext && (
        <div className="lg:col-span-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="relative flex-1">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={isPharmacy ? "Search pharmacy products…" : "Search products by name, SKU, or code…"}
                className="pl-8 h-9"
                data-testid="pos-search"
              />
            </div>
            {!isPharmacy && <Button variant="outline" onClick={() => setManualItemOpen(true)} data-testid="pos-manual-item">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Manual item
            </Button>}
          </div>

          {isHospitalServiceFeeSale ? (
            <div className="space-y-2 border-t border-border pt-3">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Hospital service fees</h3>
                <span className="text-[10px] text-muted-foreground">Qty fixed at 1</span>
              </div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                {filteredServices.length === 0 && (
                  <div className="col-span-3 border border-dashed border-border rounded-md p-8 text-center text-sm text-muted-foreground">
                    {services.length === 0 ? "No hospital service fees are available in this branch." : "No service fees match your search."}
                  </div>
                )}
                {filteredServices.map((svc) => (
                  <button
                    key={svc.service_id}
                    onClick={() => addServiceToCart(svc)}
                    className="border border-border rounded-md p-3 text-left bg-card hover:border-brand transition-colors group"
                    data-testid={`pharmacy-pos-service-${svc.service_code}`}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{svc.service_code}</div>
                    <div className="font-medium text-sm truncate group-hover:text-brand transition-colors">{svc.name}</div>
                    {svc.description && <div className="text-[11px] text-muted-foreground line-clamp-2 mt-1">{svc.description}</div>}
                    <div className="mt-2 text-right font-mono font-semibold text-sm">{formatGHS(svc.unit_price)}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {filteredInv.length === 0 && (
              <div className="col-span-3 border border-dashed border-border rounded-md p-8 text-center text-sm text-muted-foreground">
                {inventory.length === 0
                  ? isPharmacy ? "No pharmacy items with available stock. Add medicines and receive batches in Pharmacy." : "No inventory items yet. Add products in Inventory or use 'Manual item' for one-off sales."
                  : "No products match your search."}
              </div>
              )}
              {filteredInv.map((item) => {
              const salesUnit = isPharmacy ? (item.sales_unit || item.unit || "pack") : "unit";
              const retailPrice = Number(isPharmacy ? item.unit_price : item.selling_price) || 0;
              return (
                <button
                  key={isPharmacy ? item.medicine_id : item.item_id}
                  onClick={() => addToCart(item)}
                  className="border border-border rounded-md p-3 text-left bg-card hover:border-brand transition-colors group"
                  data-testid={`pos-product-${item.item_code || item.medicine_id}`}
                >
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                    {item.item_code || item.generic_name || item.dosage_form || (isPharmacy ? `${item.packaging_type || "PACK"}` : "Pharmacy item")}
                  </div>
                  <div className="font-medium text-sm truncate group-hover:text-brand transition-colors">{item.name}</div>
                  <div className="flex justify-between items-baseline mt-2">
                    <span className="text-[10px] font-mono text-muted-foreground">Qty {Number(item.quantity_on_hand)} {salesUnit}</span>
                    <span className="font-mono font-semibold text-sm">{formatGHS(retailPrice)}</span>
                  </div>
                </button>
              );
              })}
            </div>
          )}

        </div>
        )}

        {isServices && (
        <div className="lg:col-span-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="relative flex-1">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search services by name or code…"
                className="pl-8 h-9"
                data-testid="pos-services-search"
              />
            </div>
            <Button variant="outline" onClick={() => setCatalogOpen(true)} data-testid="pos-manage-services">
              <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Manage services
            </Button>
            <Button variant="outline" onClick={() => setManualItemOpen(true)} data-testid="pos-manual-item">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> One-off
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {filteredServices.length === 0 && (
              <div className="col-span-3 border border-dashed border-border rounded-md p-8 text-center text-sm text-muted-foreground">
                {services.length === 0 ? (
                  <>
                    <div className="flex items-center justify-center mb-2">
                      <Sparkles className="h-5 w-5 text-brand" />
                    </div>
                    No services in your catalog yet. Click <b>Manage services</b> to add your first one — consulting hours, subscription fees, repair jobs, etc.
                  </>
                ) : "No services match your search."}
              </div>
            )}
            {filteredServices.map((svc) => (
              <button
                key={svc.service_id}
                onClick={() => addServiceToCart(svc)}
                className="border border-border rounded-md p-3 text-left bg-card hover:border-brand transition-colors group"
                data-testid={`pos-service-${svc.service_code}`}
              >
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                  {svc.service_code}
                </div>
                <div className="font-medium text-sm truncate group-hover:text-brand transition-colors">{svc.name}</div>
                {svc.description && (
                  <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{svc.description}</div>
                )}
                <div className="flex justify-end items-baseline mt-2">
                  <span className="font-mono font-semibold text-sm">{formatGHS(svc.unit_price)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        )}

        <div className="lg:col-span-2">
          <div className="border border-border rounded-md bg-card sticky top-20">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                <div className="font-display font-semibold">{isServices ? "Current Service Sale" : "Current Sale"}</div>
              </div>
              <div className="flex items-center gap-1">
                {isServices && (
                  <Button variant="outline" size="sm" onClick={() => setManualItemOpen(true)} className="text-xs" data-testid="pos-services-add">
                    <Plus className="h-3 w-3 mr-1" /> One-off
                  </Button>
                )}
                {cart.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearCart} className="text-xs" data-testid="pos-clear">
                    Clear
                  </Button>
                )}
              </div>
            </div>

            <div className="p-4 space-y-2 max-h-72 overflow-y-auto" data-testid="pos-cart">
              {cart.length === 0 ? (
                <div className="text-center text-muted-foreground text-sm py-10">
                  Add products to start a sale
                </div>
              ) : cart.map((line, idx) => (
                <div key={idx} className="border border-border rounded-md p-2" data-testid={`pos-cart-line-${idx}`}>
                  <div className="flex items-start justify-between">
                    <div className="text-sm font-medium flex-1 mr-2">{line.name}{line.sales_unit && <span className="ml-1 text-xs font-normal text-muted-foreground">per {line.sales_unit}</span>}{line.dose && <span className="mt-1 block text-xs font-normal text-muted-foreground">{line.dose} · {line.frequency}{line.duration ? ` · ${line.duration}` : ""}{line.instructions ? ` · ${line.instructions}` : ""}</span>}</div>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeLine(idx)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between mt-1 gap-2">
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="icon" className="h-6 w-6" disabled={line.is_service_fee} onClick={() => updateQty(idx, -1)}>
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="font-mono text-sm w-8 text-center">{line.quantity}</span>
                      <Button variant="outline" size="icon" className="h-6 w-6" disabled={line.is_service_fee} onClick={() => updateQty(idx, 1)}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      value={line.unit_price}
                      onChange={(e) => updatePrice(idx, e.target.value)}
                      className="h-7 w-24 text-right font-mono text-xs"
                      data-testid={`pos-cart-price-${idx}`}
                    />
                    <div className="font-mono text-sm font-semibold w-24 text-right">
                      {formatGHS(line.quantity * line.unit_price)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-border space-y-1 font-mono text-sm">
              <Row k="Subtotal" v={formatGHS(totals.subtotal)} />
              {applyTaxes && (
                <>
                  <Row k="NHIL 2.5%" v={formatGHS(totals.nhil)} muted />
                  <Row k="GETFund 2.5%" v={formatGHS(totals.getf)} muted />
                  <Row k="VAT 15%" v={formatGHS(totals.vat)} muted />
                </>
              )}
              <div className="border-t border-border pt-2 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span data-testid="pos-total">{formatGHS(totals.total)}</span>
              </div>
            </div>

            <div className="p-4 border-t border-border space-y-3">
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applyTaxes}
                    onChange={(e) => setApplyTaxes(e.target.checked)}
                    className="h-3.5 w-3.5"
                    data-testid="pos-apply-taxes"
                  />
                  <span>Apply GRA levies</span>
                </label>
                <label className={`flex items-center gap-1.5 ${applyTaxes ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                  <input
                    type="checkbox"
                    checked={taxInclusive}
                    disabled={!applyTaxes}
                    onChange={(e) => setTaxInclusive(e.target.checked)}
                    className="h-3.5 w-3.5"
                    data-testid="pos-tax-inclusive"
                  />
                  <span>Prices are tax-inclusive</span>
                </label>
              </div>

              <div>
                <Label className="text-xs font-mono uppercase tracking-wider">{prescriptionContext ? "Customer / patient" : paymentMethod === "Credit" ? "Customer (required for credit)" : "Customer (optional)"}</Label>
                {prescriptionContext ? (
                  <div className="mt-1 space-y-1 border border-input bg-muted/30 px-3 py-2 text-sm" data-testid="pos-prescription-customer">
                    <div className="font-medium">{customer || prescriptionContext.patient?.name || "Patient"}</div>
                    <div className="text-xs text-muted-foreground">Patient reference: {prescriptionContext.patient?.patient_no || "Not available"}</div>
                  </div>
                ) : (
                  <CustomerPicker
                    value={customerId}
                    onChange={setCustomerId}
                    onCustomerChange={(selected) => setCustomer(selected?.name || "")}
                    placeholder="Walk-in or select customer..."
                    canCreate={["super_admin", "tenant_admin", "finance_manager", "accountant", "accounts_officer", "sales_rep"].includes(user?.role)}
                    testId="pos-customer"
                  />
                )}
              </div>

              <div>
                <Label className="text-xs font-mono uppercase tracking-wider">Payment Method</Label>
                <div className="grid grid-cols-2 gap-1 mt-1">
                  {PAYMENT_METHODS.filter((pm) => (isPharmacy || pm.value !== "Insurance") && (pm.value !== "Credit" || (!isPharmacy && !isServices))).map((pm) => {
                    const Icon = pm.icon;
                    return (
                      <button
                        key={pm.value}
                        onClick={() => setPaymentMethod(pm.value)}
                        className={`h-9 px-2 rounded-md border text-xs flex items-center justify-center gap-1.5 transition-colors ${
                          paymentMethod === pm.value
                            ? "border-brand bg-brand/10 text-foreground font-medium"
                            : "border-border text-muted-foreground hover:border-foreground/40"
                        }`}
                        data-testid={`pos-pay-${pm.value}`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {pm.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {paymentMethod === "Insurance" && <div className="space-y-2 border border-border p-3">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider">Insurance payer type</Label>
                  <select aria-label="Insurance payer type" className="mt-1 h-9 w-full border border-input bg-background px-2 text-sm" value={insurancePayerType} onChange={(event) => {
                    const payerType = event.target.value;
                    setInsurancePayerType(payerType);
                    setCart((current) => current.map((line) => line.prescription_id ? {
                      ...line,
                      payer_type: payerType,
                      unit_price: payerType === "NHIS" ? Number(line.nhis_unit_price || 0) : Number(line.cash_unit_price || 0),
                    } : line));
                  }}><option value="NHIS">NHIS</option><option value="PRIVATE">Private insurance</option></select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider">Insurance company / payer</Label>
                  {insuranceCompanies.length ? (
                    <Select value={insurancePayerName || ""} onValueChange={(value) => {
                      setInsurancePayerName(value);
                      const selected = insuranceCompanies.find((company) => company.company_name === value);
                      if (selected && selected.payer_type !== "BOTH") {
                        setInsurancePayerType(selected.payer_type);
                        setCart((current) => current.map((line) => line.prescription_id ? {
                          ...line,
                          payer_type: selected.payer_type,
                          unit_price: selected.payer_type === "NHIS" ? Number(line.nhis_unit_price || 0) : Number(line.cash_unit_price || 0),
                        } : line));
                      }
                    }}>
                      <SelectTrigger className="mt-1 h-9" data-testid="pos-insurance-company"><SelectValue placeholder="Choose insurer" /></SelectTrigger>
                      <SelectContent>
                        {insuranceCompanies.map((company) => (
                          <SelectItem key={company.company_id || company.company_name} value={company.company_name}>{company.company_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input required value={insurancePayerName} onChange={(event) => setInsurancePayerName(event.target.value)} placeholder="Insurance company or NHIS" className="mt-1 h-9" />
                  )}
                </div>
                {!prescriptionContext && <div><Label className="text-xs font-mono uppercase tracking-wider">Claim reference</Label><Input value={claimReference} onChange={(event) => setClaimReference(event.target.value)} placeholder="Claim / pre-authorization reference" className="mt-1 h-9" /></div>}
                <p className="text-xs text-muted-foreground">This sale is posted as unpaid Accounts Receivable. Collection can be recorded later.</p>
              </div>}

              {paymentMethod === "Credit" && <div className="border border-amber-500/40 bg-amber-500/5 p-3 text-xs" data-testid="pos-credit-notice">
                This sale will post to Accounts Receivable as unpaid. Select a customer; payment can be recorded from the sales list later.
              </div>}

              {prescriptionContext && <div>
                <Label className="text-xs font-mono uppercase tracking-wider">Patient / claim reference</Label>
                <Input value={claimReference} onChange={(event) => setClaimReference(event.target.value)} placeholder="Patient or insurer claim reference" className="mt-1 h-9" data-testid="pos-prescription-claim-reference" />
              </div>}

              {paymentMethod === "Cash" && (
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider">Amount Tendered</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={tendered}
                    onChange={(e) => setTendered(e.target.value)}
                    placeholder={String(totals.total)}
                    className="h-9 font-mono"
                    data-testid="pos-tendered"
                  />
                  {tendered > 0 && (
                    <div className="mt-1 flex justify-between font-mono text-sm">
                      <span className="text-muted-foreground text-xs">Change due</span>
                      <span className="font-semibold" data-testid="pos-change">{formatGHS(totals.change)}</span>
                    </div>
                  )}
                </div>
              )}

              {!["Insurance", "Credit"].includes(paymentMethod) && (
                <SplitPaymentEditor total={totals.total} value={splitPayments} onChange={setSplitPayments} />
              )}
              <Button
                className="w-full h-11 text-base"
                disabled={cart.length === 0 || submitting || Boolean(isHospitalServiceFeeSale && !pharmacyBillingPatientId) || Boolean(prescriptionContext && !prescriptionContext.pharmacist_id && !dispensingPharmacistId) || Boolean(prescriptionContext?.interaction_alerts?.length && !prescriptionContext?.interactions_reviewed)}
                onClick={checkout}
                data-testid="pos-checkout"
              >
                {submitting ? "Processing…" : `Complete sale • ${formatGHS(totals.total)}`}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="font-display font-semibold">{isServices ? "Services Sales" : "Recent Sales"}</div>
          <div className="text-xs text-muted-foreground">{isServices ? "Latest service receipts." : `Showing ${sales.length ? salePage * salesPageSize + 1 : 0}–${Math.min((salePage + 1) * salesPageSize, totalSales)} of ${totalSales.toLocaleString()} receipts.`}</div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead className="text-[10px] uppercase tracking-widest">Receipt</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Date</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Customer</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Cashier</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-right">Items</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-right">Total</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Payment</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {salesLoading && visibleSales.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Loading sales…</TableCell></TableRow>
            ) : visibleSales.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No sales yet</TableCell></TableRow>
            ) : visibleSales.map((s) => (
              <TableRow key={s.sale_id} data-testid={`pos-sale-${s.receipt_number}`}>
                <TableCell className="font-mono text-xs py-2">{s.receipt_number}</TableCell>
                <TableCell className="font-mono text-xs py-2">{s.date}</TableCell>
                <TableCell className="py-2 text-sm">{s.customer_name}</TableCell>
                <TableCell className="py-2 text-sm text-muted-foreground">{s.cashier_name}</TableCell>
                <TableCell className="py-2 text-right font-mono">{s.items?.length}</TableCell>
                <TableCell className="py-2 text-right font-mono font-semibold">{formatGHS(s.total)}</TableCell>
                <TableCell className="py-2 text-xs font-mono uppercase text-muted-foreground">{s.payment_method?.replace("_", " ")}</TableCell>
                <TableCell className="py-2">
                  <Badge variant="outline" className={`text-[10px] font-mono uppercase ${s.status === "Voided" ? "text-rose-700 dark:text-rose-400 border-rose-300" : "text-emerald-700 dark:text-emerald-400 border-emerald-300"}`}>
                    {s.payment_status && s.payment_status !== "PAID" ? `${s.payment_status} · ${formatGHS(s.balance)}` : s.status}
                  </Badge>
                </TableCell>
                <TableCell className="py-2 text-right space-x-1">
                  {["Insurance", "Credit"].includes(s.payment_method) && Number(s.balance) > 0 && s.status === "Completed" && <Button variant="outline" size="sm" onClick={() => { setCollectingSale(s); setCollectionForm({ amount: String(s.balance), payment_method: "Cash", reference: "" }); }}>Collect</Button>}
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setReceipt(s)} data-testid={`pos-view-${s.receipt_number}`}>
                    <Printer className="h-3.5 w-3.5" />
                  </Button>
                  {s.status === "Completed" && s.return_status !== "Full" && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Return items" onClick={() => setReturningSale(s)} data-testid={`pos-return-${s.receipt_number}`}>
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {s.status === "Completed" && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => voidSale(s)} data-testid={`pos-void-${s.receipt_number}`}>
                      <XCircle className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {totalSales > salesPageSize && (
          <div className="flex items-center justify-end gap-2 border-t border-border p-3">
            <Button variant="outline" size="sm" disabled={salePage === 0 || salesLoading} onClick={() => goToSalesPage(Math.max(0, salePage - 1))}>
              Prev
            </Button>
            <span className="text-xs text-muted-foreground">Page {salePage + 1} of {totalSalesPages}</span>
            <Button variant="outline" size="sm" disabled={salePage >= totalSalesPages - 1 || salesLoading} onClick={() => goToSalesPage(salePage + 1)}>
              Next
            </Button>
          </div>
        )}
      </div>

      <Dialog open={manualItemOpen} onOpenChange={setManualItemOpen}>
        <DialogContent data-testid="pos-manual-modal">
          <DialogHeader><DialogTitle className="font-display">Manual Item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider">Item Name</Label>
              <Input value={manualItem.name} onChange={(e) => setManualItem({ ...manualItem, name: e.target.value })} data-testid="pos-manual-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider">Quantity</Label>
                <Input type="number" step="0.01" value={manualItem.quantity} onChange={(e) => setManualItem({ ...manualItem, quantity: Number(e.target.value) })} data-testid="pos-manual-qty" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider">Unit Price</Label>
                <Input type="number" step="0.01" value={manualItem.unit_price} onChange={(e) => setManualItem({ ...manualItem, unit_price: Number(e.target.value) })} data-testid="pos-manual-price" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setManualItemOpen(false)}>Cancel</Button>
              <Button onClick={addManualItem} data-testid="pos-manual-add">Add to cart</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ReturnDialog sale={returningSale} onClose={() => setReturningSale(null)} onDone={() => { setReturningSale(null); load(); }} />
      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent className="max-w-md" data-testid="pos-receipt-modal">
          <DialogHeader><DialogTitle className="font-display">Receipt</DialogTitle></DialogHeader>
          {receipt && (
            <div ref={receiptRef} className="bg-white text-black rounded-md p-5 font-mono text-xs" id="pos-receipt-print">
              <div className="text-center border-b border-black/20 pb-2">
                <div className="font-bold text-sm">{tenant?.company_name}</div>
                <div>{tenant?.trade_name}</div>
                <div>TIN: {receipt.seller?.tin_number || tenant?.tin_number || "—"}</div>
              </div>
              <div className="text-center py-2 border-b border-black/20">
                <div className="uppercase text-[10px] tracking-widest">Sales Receipt</div>
                <div className="font-semibold">{receipt.receipt_number}</div>
                <div>{receipt.date} • {receipt.cashier_name}</div>
              </div>
              <div className="py-2 border-b border-black/20">
                <div>Customer: {receipt.customer_name}</div>
                <div>Payment: {(receipt.payments && receipt.payments.length > 1) ? receipt.payments.map((p) => `${p.method.replace("_", " ")} ${formatGHS(p.amount)}`).join(" + ") : receipt.payment_method?.replace("_", " ")}</div>
              </div>
              <div className="py-2 space-y-0.5">
                {(receipt.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between">
                    <span className="flex-1 truncate mr-2">{it.quantity} × {it.name}</span>
                    <span>{formatGHS(it.line_total || it.quantity * it.unit_price)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-black/20 pt-2 space-y-0.5">
                <div className="flex justify-between"><span>Subtotal</span><span>{formatGHS(receipt.subtotal)}</span></div>
                {receipt.nhil_amount > 0 && <div className="flex justify-between"><span>NHIL{receipt.tax_rates?.nhil ? ` (${receipt.tax_rates.nhil}%)` : ""}</span><span>{formatGHS(receipt.nhil_amount)}</span></div>}
                {receipt.getfund_amount > 0 && <div className="flex justify-between"><span>GETFund{receipt.tax_rates?.getfund ? ` (${receipt.tax_rates.getfund}%)` : ""}</span><span>{formatGHS(receipt.getfund_amount)}</span></div>}
                {receipt.vat_amount > 0 && <div className="flex justify-between"><span>VAT{receipt.tax_rates?.vat ? ` (${receipt.tax_rates.vat}%)` : ""}</span><span>{formatGHS(receipt.vat_amount)}</span></div>}
                <div className="flex justify-between font-bold border-t border-black/40 pt-1 mt-1">
                  <span>TOTAL</span><span>{formatGHS(receipt.total)}</span>
                </div>
                {Number(receipt.returned_total || 0) > 0 && (
                  <div className="flex justify-between" data-testid="pos-receipt-returned"><span>Returned (credit notes)</span><span>-{formatGHS(receipt.returned_total)}</span></div>
                )}
                {receipt.payment_status === "UNPAID" && (
                  <div className="mt-1 flex justify-between border-t border-black/20 pt-1 font-bold" data-testid="pos-receipt-balance-due">
                    <span>UNPAID BALANCE</span><span>{formatGHS(receipt.balance)}</span>
                  </div>
                )}
                {receipt.amount_tendered > 0 && (
                  <>
                    <div className="flex justify-between"><span>Tendered</span><span>{formatGHS(receipt.amount_tendered)}</span></div>
                    <div className="flex justify-between"><span>Change</span><span>{formatGHS(receipt.change_due)}</span></div>
                  </>
                )}
              </div>
              <div className="text-center pt-3 border-t border-black/20 mt-2 text-[10px]">
                Thank you for your business.
                <br />
                Powered by EGS Business Suite • {receipt.linked_journal_entry_id ? "Posted to GL" : ""}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReceipt(null)}>Close</Button>
            <Button onClick={() => printElement(`POS Receipt ${receipt.receipt_number}`, receiptRef.current, "portrait")} data-testid="pos-print-receipt">
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!collectingSale} onOpenChange={(open) => { if (!open) setCollectingSale(null); }}>
        <DialogContent className="max-w-md" data-testid="pos-receivable-collection-modal">
          <DialogHeader><DialogTitle className="font-display">{collectingSale?.payment_method === "Credit" ? "Collect credit sale" : "Collect insurance receivable"}</DialogTitle></DialogHeader>
          {collectingSale && <div className="space-y-3">
            <div className="border border-border p-3 text-sm"><p className="font-medium">{collectingSale.payment_method === "Credit" ? collectingSale.customer_name : `${collectingSale.insurance_payer_name || "Insurance payer"} · ${collectingSale.customer_name}`}</p><p className="text-xs text-muted-foreground">Receipt {collectingSale.receipt_number} · Balance {formatGHS(collectingSale.balance)}</p></div>
            <div><Label htmlFor="pos-collection-amount">Payment amount</Label><Input id="pos-collection-amount" type="number" min="0.01" max={collectingSale.balance} step="0.01" value={collectionForm.amount} onChange={(event) => setCollectionForm({ ...collectionForm, amount: event.target.value })} /></div>
            <div><Label htmlFor="pos-collection-method">Received by</Label><select id="pos-collection-method" className="mt-1 h-9 w-full border border-input bg-background px-2 text-sm" value={collectionForm.payment_method} onChange={(event) => setCollectionForm({ ...collectionForm, payment_method: event.target.value })}><option value="Cash">Cash</option><option value="Mobile_Money">Mobile Money</option><option value="Card">Card</option><option value="Bank_Transfer">Bank Transfer</option></select></div>
            <div><Label htmlFor="pos-collection-reference">Receipt / reference</Label><Input id="pos-collection-reference" value={collectionForm.reference} onChange={(event) => setCollectionForm({ ...collectionForm, reference: event.target.value })} /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setCollectingSale(null)}>Cancel</Button><Button onClick={receiveSalePayment}>Record collection</Button></div>
          </div>}
        </DialogContent>
      </Dialog>

      {isServices && <ServicesCatalogModal
        apiPath="/services"
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        onChanged={load}
      />}
      {isPharmacy && <ServicesCatalogModal
        apiPath="/hospital/services"
        onChanged={load}
      />}
      </>}
    </div>
  );
}

function Row({ k, v, muted }) {
  return (
    <div className={`flex justify-between ${muted ? "text-muted-foreground text-xs" : ""}`}>
      <span>{k}</span>
      <span>{v}</span>
    </div>
  );
}

function round2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}
